import {ChipsFilter} from '@hconnect/common/components'
import {type ChipsFilterOption} from '@hconnect/common/types'
import {IconWithTooltipButton} from '@hconnect/uikit/src/lib2'
import {Button, Pill, PillSize} from '@hm/fundamental'
import {usePermissionsQuery} from '@hproduce/permissions'
import {mdiPlus} from '@mdi/js'
import {List as ListIcon} from '@mui/icons-material'
import {Box, Divider, FormControl, FormControlLabel, Switch} from '@mui/material'
import React from 'react'
import {useTranslation} from 'react-i18next'

import {FEATURE_NAMESPACE} from '../consts'
import {TaskSource} from '../types'
import {hasCreateTaskPermission, isOnlyPomSourceSelected, isPomSourceSelected} from '../utils'

import {BulkUploadPlantActionsButton} from './BulkUploadPlantActions'

type Props = {
  isSmall: boolean
  showDescriptions: boolean
  toggleShowDescriptions: () => void
  sourceFilters: TaskSource[]
  sourceFilterOptions: ChipsFilterOption<TaskSource>[]
  onSourceFilterChange: (selected: TaskSource[]) => void
  assignedToMe: boolean
  toggleAssignedToMe: () => void
  createdByMe: boolean
  toggleCreatedByMe: () => void
  onAddTaskClick: () => void
  onBulkUploadClick: () => void
}

const FILTER_PILL_SIZE: PillSize = 'large'

export const TasksTableFilters: React.FC<Props> = ({
  isSmall,
  showDescriptions,
  toggleShowDescriptions,
  sourceFilters,
  sourceFilterOptions,
  onSourceFilterChange,
  assignedToMe,
  toggleAssignedToMe,
  createdByMe,
  toggleCreatedByMe,
  onAddTaskClick,
  onBulkUploadClick
}) => {
  const {t} = useTranslation(FEATURE_NAMESPACE)
  const {data: permissions} = usePermissionsQuery()
  const canCreateTask = hasCreateTaskPermission(permissions ?? [])
  const isOnlyPomSelected = isOnlyPomSourceSelected(sourceFilters)
  const isPomSelected = isPomSourceSelected(sourceFilters)
  return (
    <Box
      sx={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        marginBottom: 2
      }}
    >
      <Box sx={{display: 'flex', flexWrap: 'wrap', gap: 1.5}}>
        <ChipsFilter
          options={sourceFilterOptions}
          selectedOptions={sourceFilters}
          onChange={onSourceFilterChange}
          pillSize={FILTER_PILL_SIZE}
          t={t}
        />
        <Divider orientation="vertical" flexItem />
        <Pill
          text={t('tasksTable.filters.label.assignedToMe')}
          type={assignedToMe ? 'primary' : 'neutral'}
          variant={assignedToMe ? 'solid' : 'shade'}
          onClick={toggleAssignedToMe}
          size={FILTER_PILL_SIZE}
        />
        <Pill
          text={t('tasksTable.filters.label.createdByMe')}
          type={createdByMe ? 'primary' : 'neutral'}
          variant={createdByMe ? 'solid' : 'shade'}
          onClick={toggleCreatedByMe}
          size={FILTER_PILL_SIZE}
        />
      </Box>

      <Box>
        {isSmall ? (
          <FormControl>
            <IconWithTooltipButton
              data-test-id="events-show-descriptions-toggle"
              onClick={toggleShowDescriptions}
              icon={<ListIcon />}
              title={t('tasksTable.action.toggleDescriptions')}
              checked={showDescriptions}
            />
          </FormControl>
        ) : (
          <FormControlLabel
            control={
              <Switch
                checked={showDescriptions}
                data-test-id="events-show-descriptions-toggle"
                onChange={toggleShowDescriptions}
                color="primary"
              />
            }
            label={t('tasksTable.action.toggleDescriptions')}
          />
        )}
        {canCreateTask && !isSmall && !isOnlyPomSelected && (
          <Button
            type="button"
            variant="primary"
            size="large"
            icon={mdiPlus}
            iconPosition="left"
            onClick={onAddTaskClick}
            data-test-id="create_task_button"
          >
            {t('tasksTable.action.createNew')}
          </Button>
        )}
        {!isSmall && isPomSelected && (
          <BulkUploadPlantActionsButton onClick={onBulkUploadClick} className="ml-8px" />
        )}
      </Box>
    </Box>
  )
}
